import { OpenInNew, Phone, WhatsApp, Email, Person } from '@mui/icons-material'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'

import React, { useState } from 'react'
import LeadDetailsView from './LeadDetailsView';
import LeadStatusBadge from './editor/LeadStatusBadge';
import leadServices from '../leadServices';
import { useAlert } from '../../../../../../context/AlertContext';
import moment from 'moment';
import { useAppData } from '../../../../../../context/AppDataContext';
import LeadAssigneeBadge from './editor/LeadAssigneeBadge';

function LeadInfo({ leadDetails, callBack }) {

    const [viewDetails, setViewDetails] = useState(null);
    const { AppAlert } = useAlert();
    const [savingStatus, setSavingStatus] = useState(false);

    const { getMasterLabel } = useAppData();

    const handleCallBack = () => {
        setViewDetails(null);
        callBack();
    }

    const updateLead = (payload) => {
        setSavingStatus(true);
        leadServices.update(payload).then(res => {
            AppAlert(res.msg);
            callBack();
        }).catch(error => {
            AppAlert(error.msg, "error");
        }).finally(() => {
            setSavingStatus(false);
        })
    }

    const updateStatus = (statusPayload) => {
        const activity = {
            leadId: leadDetails.id,
            activityType: "Status",
            oldValue: leadDetails.status,
            newValue: statusPayload.status,
            remark: statusPayload.remark || null
        };

        const data = {
            id: leadDetails.id,
            status: statusPayload.status,
            notify: statusPayload.notify
        };
        updateLead({ data, activity });
    };

    const updateAssignee = (assigneePayload) => {
        const activity = {
            leadId: leadDetails.id,
            activityType: 'Assignee',
            oldValue: leadDetails.assignedTo,
            newValue: assigneePayload.assignedTo,
            remark: assigneePayload.remark || null,
        };
        const data = {
            id: leadDetails.id,
            assignedTo: assigneePayload.assignedTo,
        };
        updateLead({ data, activity });
    };

    return (
        <>
            <div className='p-2'>
                {/* 1. Business Name on Top & Due Date */}
                <div className='d-flex justify-content-between align-items-start mb-1'>
                    <h6 className='fw-bold text-dark mb-0 text-truncate' style={{ fontSize: '14px', maxWidth: '180px' }} title={leadDetails.businessName}>
                        {leadDetails.businessName || 'Unnamed Business'}
                    </h6>
                    {leadDetails.dueDate && (
                        <span className='badge bg-light text-purple border' style={{ fontSize: '10px' }}>
                            {moment(leadDetails.dueDate).format("MMM D")}
                        </span>
                    )}
                </div>

                {/* 2. Contact Person */}
                {leadDetails.contactPerson1 && (
                    <div className='text-muted d-flex align-items-center gap-1 mb-1' style={{ fontSize: '12px' }}>
                        <Person style={{ fontSize: '13px' }} />
                        <span className='text-truncate'>{leadDetails.contactPerson1}</span>
                    </div>
                )}

                {/* 3. Email ID (if exists) */}
                {leadDetails.contactEmail1 && (
                    <div className='text-muted d-flex align-items-center gap-1 mb-1' style={{ fontSize: '12px' }}>
                        <Email style={{ fontSize: '13px' }} />
                        <a href={`mailto:${leadDetails.contactEmail1}`} className='text-muted text-truncate text-decoration-none'>
                            {leadDetails.contactEmail1}
                        </a>
                    </div>
                )}

                {/* 4. Phone Number */}
                {leadDetails.contactPhone1 && (
                    <div className='mb-2' style={{ fontSize: '12px' }}>
                        <a href={`tel:${leadDetails.contactPhone1}`} className='text-primary text-decoration-none d-inline-flex align-items-center gap-1'>
                            <Phone style={{ fontSize: '13px' }} /> {leadDetails.contactPhone1}
                        </a>
                    </div>
                )}

                {/* 5. Assignee */}
                <div className='mb-2'>
                    <LeadAssigneeBadge
                        value={leadDetails.assignedTo}
                        callBack={updateAssignee}
                    />
                </div>

                {/* 6. Estimated Value Section */}
                <div className='mb-1 px-2 py-1 bg-light rounded d-flex justify-content-between align-items-center'>
                    <span className='text-muted' style={{ fontSize: '11px' }}>Est. Value:</span>
                    {leadDetails.estimatedValue ? (
                        <span className='text-success fw-bold' style={{ fontSize: '12px' }}>
                            ₹{Number(leadDetails.estimatedValue).toLocaleString()}
                        </span>
                    ) : (
                        <span className='text-muted' style={{ fontSize: '11px' }}>-</span>
                    )}
                </div>

                {/* 7. Lead Source Badge (Moved to Next Line) */}
                {leadDetails.source && (
                    <div className='mb-2 px-1'>
                        <span className='badge bg-secondary text-white' style={{ fontSize: '10px' }}>
                            {getMasterLabel('lead-source', leadDetails.source)}
                        </span>
                    </div>
                )}

                {/* 8. Footer Toolbar: Call, WhatsApp, Open, Status Change */}
                <div className='d-flex justify-content-between align-items-center pt-2 border-top'>
                    {/* Quick Contact Actions */}
                    <div className='d-flex align-items-center gap-2'>
                        {leadDetails.contactPhone1 && (
                            <>
                                <a 
                                    target='_blank' 
                                    rel="noreferrer"
                                    className='btn btn-outline-primary btn-sm py-0 px-2 d-flex align-items-center gap-1' 
                                    style={{ fontSize: '11px' }}
                                    href={`tel:${leadDetails.contactPhone1}`}
                                >
                                    <Phone style={{ fontSize: '12px' }} /> Call
                                </a>
                                <a 
                                    target='_blank' 
                                    rel="noreferrer"
                                    className='text-success d-flex align-items-center' 
                                    href={`https://wa.me/${leadDetails.contactPhone1}?text=Hello`}
                                    title="Open WhatsApp"
                                >
                                    <WhatsApp style={{ fontSize: '18px' }} />
                                </a>
                            </>
                        )}
                    </div>

                    {/* Modal Details Trigger & Status Editor */}
                    <div className='d-flex align-items-center gap-1'>
                        <Tooltip title="View Details">
                            <IconButton 
                                onClick={() => setViewDetails(leadDetails)} 
                                size="small"
                                className='text-secondary'
                                style={{ padding: "4px" }}
                            >
                                <OpenInNew style={{ fontSize: '16px' }} />
                            </IconButton>
                        </Tooltip>

                        {savingStatus ? (
                            <CircularProgress size={16} />
                        ) : (
                            <LeadStatusBadge value={leadDetails.status} callBack={updateStatus} caption={false} />
                        )}
                    </div>
                </div>
            </div>

            <LeadDetailsView
                leadDetails={viewDetails} 
                setLeadDetails={setViewDetails} 
                callBack={handleCallBack} 
            />
        </>
    )
}

export default LeadInfo